import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@center-pwa/store/store';
import {
  getEligibleSchemaObjects,
  getSchemaCategories,
  SchemaItemResult,
} from '@center-platform/spheres/identity';
import SphereAvatar from '../sphere-avatar/sphere-avatar';
import type { Schema } from 'types/schema';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useAvailableTypes } from '../../../hooks/useAvailableTypes';
import { useUseCases } from '../../../hooks/useUseCases';
import { useSchemaType } from '../../../hooks/useSchemaType';

interface Option {
  name: string;
  code?: string;
  avatar?: string;
}
import './SphereTypeSelector.scss';

interface SphereTypeSelectorProps {
  sphereSystemName: string; // system_name of the parent schema type
  perspectiveSystemNames: string[]; // array of perspective system_names
  onTypeSelect?: (item: SchemaItemResult) => void;
  selectedType?: string; // selected schema.system_name
  categoryDepth?: number; // depth level for categories (default: 1)
  onUseCaseChange?: (useCaseSystemNames: string[]) => void; // callback for use case changes
  useCases?: string[]; // optional use cases to filter by
  sphereId?: string; // when provided, fetches types from deployed approaches
}

type SortOption = 'recommended' | 'name' | 'newest';

const SphereTypeSelector: React.FC<SphereTypeSelectorProps> = ({
  sphereSystemName,
  perspectiveSystemNames,
  onTypeSelect,
  selectedType,
  categoryDepth = 1,
  onUseCaseChange,
  useCases = [],
  sphereId,
}) => {
  const [categories, setCategories] = useState<Schema[]>([]);
  const [validItems, setValidItems] = useState<SchemaItemResult[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(false);
  const [sortOption, setSortOption] = useState<SortOption>('recommended');
  const [enforcePerspectiveMatch, setEnforcePerspectiveMatch] =
    useState<boolean>(true);
  const [selectedUseCases, setSelectedUseCases] = useState<string[]>([]);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const userLocale = useSelector(
    (state: RootState) => state.userProfile.locale ?? 'en-US'
  );

  // Fetch schemas from deployed approaches when sphereId is provided
  const apiClients = useApiClientsOrNull();
  const {
    schemas: availableTypeSchemas,
    assigneeNodeLabel: hookAssigneeNodeLabel,
  } = useAvailableTypes(sphereId ? apiClients : null, sphereId);

  // No Redux fallback — schemas come exclusively from the deployed-approaches hook.
  // When sphereId is absent, availableTypeSchemas is empty and the selector shows nothing.
  const allSchemas = availableTypeSchemas;

  // The eligibility filter needs the parent sphere's OWN type (the assignee) to read
  // its node_label for perspective matching. That type usually isn't among the
  // available (deployed-approach-provided) types, so source the node_label from the
  // hook (resolved org-independently via globalSphereCache for `sphereId`), falling
  // back to useSchemaType. Without this, getEligibleSchemaObjects bails to [] and the
  // selector renders blank.
  const { schema: assigneeSchema } = useSchemaType(sphereSystemName);
  const assigneeNodeLabel = hookAssigneeNodeLabel ?? assigneeSchema?.node_label;

  const { useCases: allUseCases } = useUseCases();

  // Merge useCases (from props) and selectedUseCases (state), dedupe by system_name
  const mergedUseCases = Array.from(
    new Set([...(useCases ?? []), ...(selectedUseCases ?? [])])
  );

  // Dedupe allUseCases by system_name
  const useCaseList = Array.from(
    new Map(
      allUseCases.map((useCase: any) => [useCase.system_name, useCase])
    ).values()
  ).sort((a, b) => {
    const aLabel = a.name?.toLowerCase() ?? '';
    const bLabel = b.name?.toLowerCase() ?? '';
    return aLabel.localeCompare(bLabel);
  });

  useEffect(() => {
    if (sphereSystemName && perspectiveSystemNames.length > 0) {
      // 1) Fetch eligible items in one call
      const items: SchemaItemResult[] = getEligibleSchemaObjects(allSchemas, {
        assigneeSystemName: sphereSystemName,
        assigneeNodeLabel,
        perspectiveSystemNames,
        useCaseSystemNames: mergedUseCases,
        enforcePerspectiveMatch,
      });

      // 2) Use the new helper to get categories at the specified depth
      const mergedCategories = getSchemaCategories(
        items,
        allSchemas,
        categoryDepth,
        userLocale
      );

      // 3) Sort the items by node_label, then name (initial load sort; will be re-sorted by sortOption)
      const sortedItems = items.slice().sort((a, b) => {
        const aLabel = a.schema.node_label ?? '';
        const bLabel = b.schema.node_label ?? '';
        if (aLabel < bLabel) return -1;
        if (aLabel > bLabel) return 1;
        const aName = a.schema.name;
        const bName = b.schema.name;
        if (aName < bName) return -1;
        if (aName > bName) return 1;
        return 0;
      });

      setValidItems(sortedItems);
      setCategories(mergedCategories);
      setSelectedCategory(null);
      setSearchTerm('');
      setIsSearchExpanded(false);
      setSortOption('recommended'); // reset sort to default
    }
  }, [
    allSchemas,
    sphereSystemName,
    assigneeNodeLabel,
    perspectiveSystemNames,
    categoryDepth,
    userLocale,
    enforcePerspectiveMatch,
    selectedUseCases,
  ]);

  // Filtered by category (node_label) and searchTerm (name)
  const filteredItems = validItems.filter((item) => {
    const byCategory = selectedCategory
      ? item.schema.node_label === selectedCategory
      : true;

    const bySearch = searchTerm
      ? item.schema.name.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    return byCategory && bySearch;
  });

  // Now sort filteredItems according to sortOption
  const sortedByOption = filteredItems.slice().sort((a, b) => {
    if (sortOption === 'recommended') {
      // Descending by weightScore, tie-breaker: localized name
      if (b.weightScore !== a.weightScore) {
        return b.weightScore - a.weightScore;
      }
      const aName =
        a.schema.schema_name_translations?.[userLocale] ?? a.schema.name;
      const bName =
        b.schema.schema_name_translations?.[userLocale] ?? b.schema.name;
      return aName.localeCompare(bName, userLocale);
    }

    if (sortOption === 'name') {
      // Ascending by localized name
      const aName =
        a.schema.schema_name_translations?.[userLocale] ?? a.schema.name;
      const bName =
        b.schema.schema_name_translations?.[userLocale] ?? b.schema.name;
      return aName.localeCompare(bName, userLocale);
    }

    // sortOption === 'newest'
    // Descending by created_at
    const aTime = Date.parse(a.schema.created_at);
    const bTime = Date.parse(b.schema.created_at);
    if (bTime !== aTime) {
      return bTime - aTime;
    }
    // tie-break by localized name
    const aName2 =
      a.schema.schema_name_translations?.[userLocale] ?? a.schema.name;
    const bName2 =
      b.schema.schema_name_translations?.[userLocale] ?? b.schema.name;
    return aName2.localeCompare(bName2, userLocale);
  });

  // 3-column grid layout
  const gridColumns = 3;
  const remainder = sortedByOption.length % gridColumns;
  const fillersNeeded = remainder === 0 ? 0 : gridColumns - remainder;

  // Create filler SchemaItemResult objects
  const fillers: SchemaItemResult[] = Array.from(
    { length: fillersNeeded },
    (_, idx) => ({
      schema: {
        schema_id: `filler-${idx}`,
        system_name: '',
        name: '',
        is_abstract: false,
        parent_schema_id: null,
        perspectives: [],
        use_case_tags: [],
        version_id: '',
        description: null,
        node_label: '',
        glyph: '',
        glyph_color: null,
        schema_name_translations: {},
        schema_description_translations: {},
        parent_version_id: null,
        override_node_label: false,
        is_system_schema: false,
        tenant_id: '',
        created_by: '',
        updated_by: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        default_context: '',
        properties: {} as any,
        permissions: [] as any[],
        metaspaces: [] as any[],
        automations: [] as any[],
        status: null,
        flow: null,
      } as unknown as Schema,
      weightScore: 0,
      matchedPerspectives: [],
      matchedUseCases: [],
    })
  );

  const typesWithFillers = [...sortedByOption, ...fillers];

  // Handlers for search
  const handleSearchIconClick = () => {
    setIsSearchExpanded(true);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      setIsSearchExpanded(false);
    }
  };

  useEffect(() => {
    if (isSearchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchExpanded]);

  // Handler for use case selection changes
  const handleUseCaseChange = (selectedOptions: Option[] | null) => {
    const useCaseSystemNames =
      selectedOptions?.map((option) => option.code ?? option.name) ?? [];
    setSelectedUseCases(useCaseSystemNames);
    onUseCaseChange?.(useCaseSystemNames);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isSearchExpanded &&
        searchBarRef.current &&
        !searchBarRef.current.contains(event.target as Node)
      ) {
        setIsSearchExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchExpanded]);

  return (
    <div className="sphere-type-selector">
      {/* Categories and Search Bar */}
      <div className="categories">
        {categories.map((category) => (
          <button
            key={category.schema_id}
            className={`category-button ${
              selectedCategory === category.node_label ? 'active' : ''
            }`}
            onClick={() =>
              setSelectedCategory(
                selectedCategory === category.node_label
                  ? null
                  : category.node_label
              )
            }
          >
            <SphereAvatar
              glyph={category.glyph ?? 'default-icon'}
              glyphColor={category.glyph_color ?? undefined}
              decorative
            />
            <span>
              {category.schema_name_translations?.[userLocale] ?? category.name}
            </span>
          </button>
        ))}

        {/* Search bar as last pill */}
        <div
          className={`search-bar ${
            isSearchExpanded ? 'expanded' : 'collapsed'
          }`}
          ref={searchBarRef}
        >
          <span
            className="pi pi-search search-icon"
            onClick={handleSearchIconClick}
            role="button"
            tabIndex={0}
            aria-label="Toggle Search"
            aria-expanded={isSearchExpanded}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSearchIconClick();
              }
            }}
          />
          {isSearchExpanded && (
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              aria-label="Search for a type"
              ref={searchInputRef}
            />
          )}
        </div>
      </div>

      {/* Valid Types Grid */}
      <div className="valid-types-grid">
        {typesWithFillers.map((item) => {
          const isFiller =
            item.schema.system_name === '' && item.schema.name === '';
          const isDisabled =
            !isFiller && selectedType === item.schema.system_name;

          return (
            <div
              key={item.schema.schema_id}
              className={`type-item ${isDisabled ? 'disabled' : ''} ${
                isFiller ? 'filler' : ''
              }`}
              onClick={() => {
                if (!isDisabled && !isFiller) {
                  onTypeSelect?.(item);
                }
              }}
              aria-disabled={isDisabled || isFiller}
              role="button"
              tabIndex={isDisabled || isFiller ? -1 : 0}
              onKeyDown={(e) => {
                if (
                  !isDisabled &&
                  !isFiller &&
                  (e.key === 'Enter' || e.key === ' ')
                ) {
                  onTypeSelect?.(item);
                }
              }}
            >
              {!isFiller && (
                <>
                  <SphereAvatar
                    glyph={item.schema.glyph ?? 'default-icon'}
                    glyphColor={item.schema.glyph_color ?? undefined}
                    decorative
                  />
                  <span>
                    {item.schema.schema_name_translations?.[userLocale] ??
                      item.schema.name}
                  </span>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SphereTypeSelector;
